import { format, isSameDay } from 'date-fns';
import { RefObject } from 'react';

import { TimelineRange, TimelineScaleTypes } from '@/types';
import { tv } from '@/utils/styles';

import { buildTimelineRangebar } from '../utils';

export interface TimelineRangeBarProps {
  range: TimelineRange;
  currentDate: Date;
  scale: TimelineScaleTypes;
  gridSize: number;
  markerRef: RefObject<HTMLDivElement>;
}

const TimelineRangeBar = ({ range, currentDate, scale, gridSize, markerRef }: TimelineRangeBarProps) => {
  const days = buildTimelineRangebar(range, scale);
  const canvasWidth = gridSize * range.days;

  const {
    base,
    rangeBar,
    rangeBorder,
    dayCell,
    monthMarker,
    monthLabel,
    gridLine,
    dayMarker,
    dayLabel,
    todayMarker,
    todayLabel,
    todayBar,
  } = styles();

  return (
    <div className={base()} style={{ width: `${canvasWidth}px` }}>
      <div className={rangeBar()} style={{ gridAutoColumns: `${gridSize}px` }}>
        <span className={rangeBorder()} />
        {days.map((day) => {
          const isStartOfMonth = !!day.monthLabel;
          const dayAsDate = new Date(day.key);
          const isCurrentDate = isSameDay(dayAsDate, currentDate);

          return (
            <div key={day.key} className={dayCell({ isWeekend: day.isWeekend })} ref={isCurrentDate ? markerRef : null}>
              {day.hasGridLine && <span className={gridLine({ isMonth: isStartOfMonth })} />}
              {isStartOfMonth && (
                <div className={monthMarker()}>
                  <span className={monthLabel()}>{day.monthLabel}</span>
                </div>
              )}
              {day.shouldShowDayLabel && (
                <div className={dayMarker()}>
                  <span className={dayLabel()}>{day.dayLabel}</span>
                </div>
              )}
              {isCurrentDate && (
                <>
                  <span className={todayBar()} />
                  <div className={todayMarker()}>
                    <span className={todayLabel()}>{format(dayAsDate, 'MMM d')}</span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative h-full bg-grey-50',
    rangeBar: 'grid h-full grid-flow-col',
    rangeBorder: 'absolute left-0 right-0 top-14 z-4 border-[1.5px] border-b border-grey-300',
    dayCell: 'relative h-full',
    monthMarker: 'absolute left-0 top-4 z-4 text-sm leading-none text-text-secondary',
    monthLabel: 'whitespace-nowrap pl-2 font-medium',
    gridLine: 'absolute -bottom-1 -top-1 left-0 z-2 border-l border-dotted border-grey-200',
    dayMarker: 'absolute left-1/2 top-8 z-3 -translate-x-1/2 pt-0 text-xs leading-none',
    dayLabel: 'text-grey-500',
    todayMarker: 'absolute left-1/2 top-[30px] z-5 flex w-14 -translate-x-1/2 justify-center',
    todayLabel:
      'grid h-full whitespace-nowrap rounded bg-lightBlue-400 px-1 py-0.5 text-xs uppercase leading-none text-white',
    todayBar:
      'absolute -bottom-1 -top-1 left-0 right-0 z-3 bg-lightBlue-100/30 after:absolute after:bottom-0 after:left-1/2 after:top-0 after:-translate-x-1/2 after:border-l after:border-lightBlue-400 after:content-[""]',
  },
  variants: {
    isMonth: {
      true: {
        gridLine: 'border-grey-350',
      },
      false: '',
    },
    isWeekend: {
      true: {
        dayCell: 'bg-grey-100',
      },
      false: '',
    },
  },
});

export { TimelineRangeBar };
